// Clinic-portal contract. The doctor-facing half of the workflow module.
//
// GOVERNING RULE: a clinic is a CUSTOMER, not a colleague. Two consequences encoded here —
//  1. They never see internal stage vocabulary ("segmentation", "for_mubeen_awais_review"). Internal
//     stages collapse onto a short public milestone track (WF_CLINIC_MILESTONES).
//  2. They never see internal commercial notes. `WfPortalCase` is a deliberately NARROW projection of
//     WfCase — adding a field here exposes it to customers, so add nothing casually.
import { z } from 'zod';
import type { WfPriority, WfReqStatus } from './workflow.js';
import type { ApprovalMedia } from './compliance.js';

// ---------------------------------------------------------------------------------------------
// Public progress track
// ---------------------------------------------------------------------------------------------

export const WF_CLINIC_MILESTONES = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'review', label: 'Under Review' },
  { key: 'design', label: 'In Design' },
  { key: 'approval', label: 'Your Approval' },
  { key: 'production', label: 'In Production' },
  { key: 'qc', label: 'Quality & Packing' },
  { key: 'ready', label: 'Ready for Dispatch' },
  { key: 'dispatched', label: 'Dispatched' },
] as const;
export type WfClinicMilestone = (typeof WF_CLINIC_MILESTONES)[number]['key'];

/** Which public milestone each internal stage maps to. Unlisted stages fall back to 'review'. */
export const WF_CLINIC_MILESTONE_OF: Record<string, WfClinicMilestone> = {
  new_received: 'submitted',
  awaiting_records: 'review',
  from_previous_records: 'review',
  only_assessment: 'review',
  scan_export: 'design',
  segmentation: 'design',
  for_mubeen_awais_review: 'design',
  for_dr_ali_review: 'design',
  share_simulation: 'approval',
  awaiting_approval_pt: 'approval',
  approved: 'production',
  proceed_for_printing: 'production',
  printing_in_progress: 'production',
  print_complete: 'production',
  punching: 'production',
  cutting: 'production',
  final_touch_up: 'production',
  in_finishing: 'production',
  retainers: 'production',
  twin_block: 'production',
  zirconia_crowns: 'production',
  appliances: 'production',
  packaging: 'qc',
  ready_for_dispatch: 'ready',
  dispatched: 'dispatched',
};

/** The single line of status text a doctor sees. Internal names never appear. */
export const WF_CLINIC_STATUS_LABELS: Record<string, string> = {
  new_received: 'Submitted',
  awaiting_records: 'Awaiting Records',
  from_previous_records: 'Under Review',
  only_assessment: 'Under Assessment',
  scan_export: 'In Design',
  segmentation: 'In Design',
  for_mubeen_awais_review: 'In Design',
  for_dr_ali_review: 'In Design',
  share_simulation: 'Simulation Shared',
  awaiting_approval_pt: 'Awaiting Your Approval',
  approved: 'Approved - In Production',
  proceed_for_printing: 'In Production',
  printing_in_progress: 'In Production',
  print_complete: 'In Production',
  punching: 'In Production',
  cutting: 'In Production',
  final_touch_up: 'In Production',
  in_finishing: 'In Production',
  retainers: 'In Production',
  twin_block: 'In Production',
  zirconia_crowns: 'In Production',
  appliances: 'In Production',
  packaging: 'Quality & Packing',
  ready_for_dispatch: 'Ready for Dispatch',
  dispatched: 'Dispatched',
  on_hold: 'On Hold',
  case_cancelled: 'Cancelled',
};

export const clinicStatusLabel = (stageKey: string): string => WF_CLINIC_STATUS_LABELS[stageKey] ?? 'In Progress';
export const clinicMilestoneOf = (stageKey: string): WfClinicMilestone => WF_CLINIC_MILESTONE_OF[stageKey] ?? 'review';
/** Stages where the ball is in the doctor's court — drives the "Action needed" panel. */
export const WF_CLINIC_ACTION_STAGES = ['share_simulation', 'awaiting_approval_pt', 'awaiting_records'] as const;

// ---------------------------------------------------------------------------------------------
// The narrow case projection
// ---------------------------------------------------------------------------------------------

export interface WfPortalCase {
  id: string;
  caseNumber: string;
  patientName: string;
  patientCode: string | null;
  caseTypes: string[];
  /** Doctor-facing status text — never the internal stage key. */
  clinicStatus: string;
  milestone: WfClinicMilestone;
  isTerminal: boolean;
  isCancelled: boolean;
  isOnHold: boolean;
  priority: WfPriority;
  receivedAt: string;
  dueDate: string | null;
  isOverdue: boolean;
  dispatchedAt: string | null;
  trackingNumber: string | null;
  courier: string | null;
  /** The doctor's own instructions, echoed back. NOT internal/production/accounts notes. */
  notes: string | null;
  patientInstructions: string | null;
  iprStatus: WfReqStatus;
  etchingStatus: WfReqStatus;
  /** True when the lab has shared a simulation/video and is waiting on the doctor. */
  awaitingYourApproval: boolean;
  approvalState: string;
  /** Which models and how many aligners the lab planned; null until the lab sets them. */
  arch: 'upper' | 'lower' | 'both' | null;
  upperTrays: number | null;
  lowerTrays: number | null;
  trays: number | null;
}

/**
 * The case proforma: what the doctor signs off (models, aligner numbers, price) and what goes in
 * the box with the aligners. One shape for both, built on the server from the master record and
 * the lab's pricing so the portal and the lab print the same figures.
 */
export interface WfCaseProforma {
  caseNumber: string;
  patientName: string;
  patientCode: string | null;
  doctorName: string | null;
  clinicName: string | null;
  groupId: string | null;
  caseTypes: string[];
  arch: 'upper' | 'lower' | 'both' | null;
  archLabel: string;
  upperTrays: number | null;
  lowerTrays: number | null;
  trays: number;
  /** Price lines in whole rupees; null when no aligner count has been set yet. */
  perTrayRupees: number | null;
  grossRupees: number | null;
  discountLabel: string | null;
  discountRupees: number;
  expressLabel: string | null;
  expressRupees: number;
  /** Hand edits on the live invoice (its total minus the priced lines above). 0 when none. */
  adjustmentRupees: number;
  /** The case's live invoice total when there is one - it wins over the formula. */
  totalRupees: number | null;
  invoiceNumber: string | null;
  priority: WfPriority;
  dueDate: string | null;
  receivedAt: string;
  setupVersion: number;
  approvalState: string;
  /** Doctor's own instructions, for the box slip. */
  patientInstructions: string | null;
  preparedAt: string;
}

export interface WfPortalFile {
  id: string;
  originalName: string;
  description: string | null;
  category: string;
  sizeBytes: number;
  uploadedAt: string;
  /** 'clinic' when the doctor sent it, 'lab' when the lab produced it. */
  side: 'clinic' | 'lab';
}

export interface WfPortalTimelineEntry {
  id: string;
  at: string;
  label: string;
  detail: string | null;
}

export interface WfPortalRequest {
  id: string;
  caseId: string;
  kind: 'change' | 'treatment' | 'cancel';
  description: string;
  status: 'open' | 'accepted' | 'declined' | 'done';
  raisedByName: string | null;
  raisedAt: string;
  resolution: string | null;
  resolvedAt: string | null;
}

export interface WfPortalMessage {
  id: string;
  body: string;
  authorName: string | null;
  side: 'clinic' | 'lab';
  createdAt: string;
}

export interface WfPortalCaseView {
  case: WfPortalCase;
  milestones: typeof WF_CLINIC_MILESTONES;
  files: WfPortalFile[];
  timeline: WfPortalTimelineEntry[];
  requests: WfPortalRequest[];
  messages: WfPortalMessage[];
  /** Approval can only be given once the lab has shared something to approve. */
  canApprove: boolean;
  approvalBlockedReason: string | null;
  /** The model video / simulation the lab put in front of the doctor, if any. */
  approvalMedia: ApprovalMedia | null;
  /** Models, aligner numbers and price - what the approval actually covers. */
  proforma: WfCaseProforma | null;
}

export interface WfPortalDashboard {
  doctorName: string | null;
  clinicName: string | null;
  doctorCode: string | null;
  totals: { total: number; active: number; underReview: number; inDesign: number; inProduction: number; ready: number; dispatched: number; onHold: number };
  actionNeeded: WfPortalCase[];
  recent: WfPortalCase[];
  openRequests: number;
  unreadMessages: number;
  /** Outstanding balance in paisa (charges − settled). Negative means the clinic is in credit. */
  balance: number;
  /** Submitted payments the lab has not verified yet — shown alongside, never netted off. */
  pendingPayments: number;
}

// ---------------------------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------------------------

const txt = (max: number) => z.string().trim().max(max).nullish();

export const wfPortalRequestSchema = z.object({
  kind: z.enum(['change', 'treatment', 'cancel']),
  description: z.string().trim().min(3, 'Please describe what you need').max(4000),
});
export const wfPortalApproveSchema = z.object({ comments: txt(2000) });
export const wfPortalMessageSchema = z.object({ body: z.string().trim().min(1).max(4000) });

// ---------------------------------------------------------------------------------------------
// Notices
// ---------------------------------------------------------------------------------------------

export interface WfNotice {
  id: string;
  title: string;
  body: string;
  pinned: boolean;
  isActive: boolean;
  createdAt: string;
}
export const wfNoticeSchema = z.object({
  title: z.string().trim().min(1).max(160),
  body: z.string().trim().min(1).max(8000),
  pinned: z.boolean().optional(),
  isActive: z.boolean().optional(),
});

// ---------------------------------------------------------------------------------------------
// Scan booking
// ---------------------------------------------------------------------------------------------

export interface WfScanSlot {
  id: string;
  startsAt: string;
  durationMin: number;
  location: string | null;
  capacity: number;
  booked: number;
  isActive: boolean;
}
export interface WfScanBooking {
  id: string;
  slotId: string;
  startsAt: string;
  location: string | null;
  patientName: string;
  contact: string | null;
  note: string | null;
  caseId: string | null;
  status: 'booked' | 'cancelled' | 'done';
  cancelReason: string | null;
  bookedAt: string;
  doctorName?: string | null;
}
export const wfScanSlotSchema = z.object({
  startsAt: z.string().min(1),
  durationMin: z.number().int().min(5).max(600).optional(),
  location: txt(160),
  capacity: z.number().int().min(1).max(50).optional(),
  isActive: z.boolean().optional(),
});
export const wfScanBookSchema = z.object({
  slotId: z.string().min(1),
  patientName: z.string().trim().min(1).max(160),
  contact: txt(60),
  caseId: z.string().nullish(),
  note: txt(2000),
});
export const wfScanCancelSchema = z.object({ reason: z.string().trim().min(1).max(500) });

// ---------------------------------------------------------------------------------------------
// Clinical preferences + clinic profile
// ---------------------------------------------------------------------------------------------

export interface WfClinicPrefs {
  defaultCaseType: string | null;
  defaultArch: string | null;
  defaultPriority: WfPriority | null;
  iprPreference: WfReqStatus | null;
  etchingPreference: WfReqStatus | null;
  attachmentsPreference: string | null;
  trimLine: string | null;
  staging: string | null;
  notes: string | null;
}
export const wfClinicPrefsSchema = z.object({
  defaultCaseType: txt(64),
  defaultArch: txt(16),
  defaultPriority: z.enum(['normal', 'urgent', 'emergency']).nullish(),
  iprPreference: z.enum(['not_required', 'required', 'pending', 'completed']).nullish(),
  etchingPreference: z.enum(['not_required', 'required', 'pending', 'completed']).nullish(),
  attachmentsPreference: txt(160),
  trimLine: txt(160),
  staging: txt(160),
  notes: txt(4000),
});

export interface WfClinicProfile {
  clinicName: string;
  doctorName: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  city: string | null;
  address: string | null;
  code: string;
}
export const wfClinicProfileSchema = z.object({
  clinicName: z.string().trim().min(1).max(160),
  doctorName: txt(120),
  phone: txt(40),
  whatsapp: txt(40),
  email: txt(160),
  city: txt(80),
  address: txt(400),
});

// ---------------------------------------------------------------------------------------------
// Assessment (photos + complaint, no full case yet)
// ---------------------------------------------------------------------------------------------

export const wfAssessmentSchema = z.object({
  patientFirstName: z.string().trim().min(1).max(120),
  patientLastName: txt(120),
  chiefComplaint: z.string().trim().min(3, 'Describe the chief complaint').max(4000),
  phone: txt(40),
  notes: txt(4000),
});
